import {QA_RULE_LEVELS} from "../../components/organisms/QARuleForm/constants";
import {QueryQARules, SearchQARuleEntries} from "../../queries/QAruleData";
import {QueueFilter} from "../../components/organisms/CollectionSettings/types";
import {getGroups} from "../../queries/groups";
import {getViewById} from "../../queries/views";
import {collectionNamesQuery, cqaWorklistSearchFieldOptions, reverseServiceLineMapper} from "../common";
import { CollectionNames } from "../../queries/profileData";
import { GraderWorkItem, OptionEntry } from "../../types/cqa";
import { CQARiskDictionaries } from "../../types/dictionary";
import type { ProjectRubricVersionChangeImpact } from "../../types/sd";
import {
    Rubric,
    RubricVersion,
    CloneRubricPayload,
    CloneRubricVersionPayload,
    CodeWorkflow,
    Question,
    WorkflowQuestion,
    WorkflowTrigger,
    CodeWorkflowType,
    FullRubricVersion,
    InclusionMode,
} from "../../types/rubric";
import { WorkflowTriggerAction } from "../../types/rubric";
import type { AnswerSet, AnswerValue } from "../../types/answerSet";
import type { WorkflowEvaluationRequest, WorkflowEvaluationResponse } from "../../types/workflowEvaluation";
import { LABEL_SPECIFIC_COUNT, LABEL_SPECIFIC_PERCENTAGE, LABEL_EQUAL_PERCENTAGES, LABEL_MULTI_USER } from "../../components/organisms/QARuleForm/constants";
import type {
    ResolverConfigDto,
    ResolverResponse,
    SetActiveResolverConfigRequest,
    StatelessResolverRequest,
} from "../../types/resolver";

interface CallResponse {
    columnNames: string[];
    rowValues: any[];
    numRows: number
}

/**
 * Safely parses the tokenPayload from sessionStorage.
 * Returns null when the key is absent or the value is not valid JSON.
 */
export const getTokenPayload = (): Record<string, any> | null => {
    const raw = sessionStorage.getItem("tokenPayload");
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

// Which column do we look at to get value
export const dictionaryValueMap = {
    "Facility": "Facility",
    "Patient Class": "Patient Class",
    "Patient Type": "Patient Type",
    "Note Specialty": "Note Specialty",
    "Billing Area": "Billing Area"
}

export const codeTypeMap = {
    "ICD Codes": "ICD",
    "CPT Codes": "CPT",
    "Modifiers": "MODIFIER"
}

const cmxDateFormatter = (date: Date) => {
    const offset = date.getTimezoneOffset();
    const offsetDate = new Date(date.getTime() - (offset * 60 * 1000));
    return offsetDate.toISOString().split('T')[0];
}

//region General API
export const getUnrestrictedDictionary = async (tenantId: string, name: string, abortSignal) => {
    try {
        let effectiveDate = new Date();
        let formattedDate = cmxDateFormatter(effectiveDate);

        const searchParams = new URLSearchParams({
            tenantId: tenantId,
            effectiveDate: formattedDate,
            dictionaryName: name,
        });

        const response = await fetch(`/dictionary/row/unrestricted/v2?${searchParams.toString()}`,
            {
                signal: abortSignal,
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
                }
            });
        const body: CallResponse = await response.json();
        return body;
    } catch (err) {
        console.error(`Error getting dictonaries: ${err}`);
        return {
            columnNames: [],
            rowValues: [],
            numRows: 0
        };
    }
}

export const fetchCodeSearch = async (tenantId: string, searchTerm: string, codeType: string, abortSignal) => {
    const effectiveDate = new Date();
    const formattedDate = cmxDateFormatter(effectiveDate);

    try {
        const response = await fetch(`/dictionary/code/v2?tenantId=${tenantId}`, {
            signal: abortSignal,
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            },
            body: JSON.stringify({
                "codeType": codeType,
                "searchTerm": searchTerm,
                "effectiveDate": formattedDate,
            })
        });
        return await response.json();
    }
    catch (err) {
        console.error(`Error fetching code search: ${err}`);
        return {
            content: []
        }
    }
}

export const utilGetGroups = async (client) => {
    try {
        const response = await client.query({
            query: getGroups,
            fetchPolicy: "no-cache",
        });

        return response.data?.Groups?.data;
    }
    catch (err) {
        console.error(`Error fetching groups: ${err}`);
    }
}


export const utilGetQueues = async (client, serviceLine, healthSystemId) => {
    try {
      const profileQuery = collectionNamesQuery(serviceLine, healthSystemId);
      const response = await client.mutate({
        mutation: CollectionNames,
        variables: {
          profileQuery: profileQuery,
        },
      });
      return response?.data?.QueryProfile;
    } catch (err) {
      console.error(`Error fetching queues: ${err}`);
      return [];
    }
};

export const utilGetViewById = async (client, directoryType, viewId) => {
    try {
        const response = await client
            .query({
                query: getViewById,
                variables: {
                    widgetType: directoryType,
                    id: viewId,
                },
                fetchPolicy: "no-cache",
            })

        return response.data?.ViewById?.data;
    }
    catch (err) {
        console.error(`Error fetching view: ${err}`);
        return [];
    }
}

export const getQaRules = async (client, directoryType, service, filters = []) => {
    // Default filtering checks for Active queues
    let requestBody: QueueFilter = {
        combinator: "AND",
        filters: filters
    };

    requestBody = {
        ...requestBody,
        filters: requestBody.filters.concat(
            {
                key: "level",
                operation: "eq",
                value: QA_RULE_LEVELS[directoryType] ?? 0
            },
            {
                key: "service",
                operation: "eq",
                value: service
            }
        ),
        level: parseInt(QA_RULE_LEVELS[directoryType] ?? 0),
        service: service
    }

    const searchVar = 'SearchQarule'
    try {
        const response = await client.mutate({
            mutation: SearchQARuleEntries,
            variables: {
                request: requestBody,
            },
            fetchPolicy: "no-cache",
        })

        return response.data[searchVar]?.data;
    }
    catch (err) {
        console.error(`Error fetching QA rules: ${err}`);
    }
}
//endregion

//region Coder Notes
export const apiGetCoderNotesByEncounterUid = async (encounterUid): Promise<AmplifyAPI.CoderNoteRow[] | null> => {
    try {
        const response = await fetch(`/coding-api/coder_notes/v1?encounter_uid=` + encounterUid, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            }
        });
        
        if (!response.ok) {
            console.error("Error fetching coder notes: ", response.statusText);
            return null;
        }
        
        const body = await response.json();
        return body;
    }
    catch (e) {
        console.error("Error fetching coder notes: ", e);
        return null;
    }
}

export const apiGetCoderNotesByPatientMrn = async (patientMrn): Promise<AmplifyAPI.CoderNoteRow[] | null> => {
    try {
        const response = await fetch(`/coding-api/coder_notes/v1?patient_mrn=` + patientMrn, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            }
        });

        if (!response.ok) {
            console.error("Error fetching coder notes: ", response.statusText);
            return null;
        }

        const body = await response.json();
        return body;
    }
    catch (e) {
        console.error("Error fetching coder notes: ", e);
        return null;
    }
}

export const apiGetBulkCoderNotes = async (identifiers: string[], identifierType: 'patient_mrns' | 'accession_numbers'): Promise<AmplifyAPI.CoderNoteRow[] | null> => {
    try {
        const response = await fetch(`/coding-api/coder_notes/bulk/v1`, {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            },
            body: JSON.stringify({
                [identifierType]: identifiers,
                'size': 100
            })
        });

        if (!response.ok) {
            console.error("Error fetching coder notes: ", response.statusText);
            return null;
        }

        const body = await response.json();
        return body.content;
    }
    catch (e) {
        console.error("Error fetching coder notes: ", e);
        return null;
    }
}

export type ExcludeCaseRequest = {
  processInstanceGuid: string;
  isChargeable: boolean;
};

export const apiExcludeCases = async (
  cases: ExcludeCaseRequest[],
  serviceLine: string
): Promise<void> => {
  try {
    const response = await fetch(
      `/coding-api/processinstance/${serviceLine}/chargeable/bulk/v1`,
      {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "Authorization": sessionStorage.getItem("authToken") || "",
        },
        body: JSON.stringify({ updates: cases }),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to exclude cases. Status: ${response.status} ${response.statusText}`
      );
    }

   } catch (error) {
    console.error("Error excluding cases:", error);
    throw error; 
  }
};

export const apiGetCaseInstance = async (cmxCaseUid): Promise<CMxAPI.Case> => {
    try {
        const response = await fetch(`/api/cases/IPC/caseinstance/${cmxCaseUid}/details/v1?evidence=true&processTypes=PREDICTION&processTypes=ADDENDUM`, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            }
        });
        
        if (!response.ok) {
            console.error("Error fetching case instance: ", response.statusText);
            return null;
        }
        
        return await response.json();
    }
    catch (e){
        console.error("Error fetching case document: ", e);
        return null;
    }
}

export const getAllQARules = async (client, healthSystemId, serviceLine) => {
    let qaRules = [];
    const qaruleQuery = {
      query: `select collection_id as value, name as label from collection where type='QARULE_COLLECTION' and health_system_id='${healthSystemId}' and service='${serviceLine}' and status='ACTIVE';`,
    };
    try {
      const response = await client.mutate({
        mutation: QueryQARules,
        variables: { qaruleQuery },
      });
      qaRules = response?.data?.QueryQarule;  
    } catch (err) {
      console.error("error fetching rule names", err);
    }
    return qaRules;
  };
//endregion

export const getCqaGraderWorkitemsBatch = async (sdCaseIds: string[]): Promise<GraderWorkItem[]> => {
    try {
        const response = await fetch(`/api/cqa-service/v1/grader-workitem/batch`, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            },
            method: "POST",
            body: JSON.stringify({
                sdCaseIds
            })
        });

        if (!response.ok) {
            console.error("Error POST /api/cqa-service/v1/grader-workitem/batch");
            return [];
        }

        const body = await response.json();
        return body;
    }
    catch (e) {
        console.error("Error POST /api/cqa-service/v1/grader-workitem/batch", e);
        return [];
    }
};

export const apiBulkUpdateGraderWorkitems = async (updates: Array<{ sdCaseId: string; status: string }>) => {
    if (!updates || updates.length === 0) {
        throw new Error('Updates array cannot be empty');
    }

    try {
        const response = await fetch('/api/cqa-service/v1/grader-workitem/bulk', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `${sessionStorage.getItem('authToken')}`,
            },
            body: JSON.stringify({ updates })
        });

        if (!response.ok) {
            const errorText = await response.text();
            let errorMessage = `Failed to update grader work items. Status: ${response.status}`;

            try {
                const errorJson = JSON.parse(errorText);
                if (errorJson.reason) {
                    errorMessage = errorJson.reason;
                }
            } catch {
                // If parsing fails, use default error message
            }

            throw new Error(errorMessage);
        }

        return await response.json();
    } catch (error) {
        console.error('Error in apiBulkUpdateGraderWorkitems:', error);
        throw error;
    }
};

export type CoderNoteBatchEntry = {
    note_content: string;
    lookup_reference: string;
    lookup_type: string;
};

export const apiBatchAddCoderNotes = async (notes: CoderNoteBatchEntry[]) => {
    if (!notes || notes.length === 0) {
        console.warn('apiBatchAddCoderNotes called with empty notes array; skipping request.');
        return;
    }

    const response = await fetch('/coding-api/coder_notes/batch/v1', {
        method: 'POST',
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(notes),
    });

    return handleJsonResponse<unknown>(response);
};

const buildAuthorizedJsonHeaders = () => ({
    "Accept": "application/json",
    "Content-Type": "application/json",
    "Authorization": `${sessionStorage.getItem("authToken")}`,
});

const handleJsonResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
        const errorText = await response.text();
        let reason = `Request failed with status ${response.status}`;
        try {
            const parsed = JSON.parse(errorText);
            if (parsed?.reason) {
                reason = parsed.reason;
            }
        } catch {
            if (errorText) {
                reason = errorText;
            }
        }
        throw new Error(reason);
    }

    if (response.status === 204) {
        return undefined as T;
    }

    return await response.json();
};

const normalizeRubricVersion = (version: RubricVersion): RubricVersion => {
    if (!version) {
        return version;
    }

    const workflows = version.codeWorkflows ?? {};

    return {
        ...version,
        procedureWorkflowId: workflows.PROCEDURE?.[0] ?? null,
        modifierWorkflowId: workflows.MODIFIER?.[0] ?? null,
        diagnosisWorkflowId: workflows.DIAGNOSIS?.[0] ?? null,
        caseWorkflowId: workflows.CASE?.[0] ?? null,
        spanWorkflowId: workflows.SPAN?.[0] ?? null,
    };
};

const buildCodeWorkflowPayload = (payload: {
    procedureWorkflowId?: number | null;
    modifierWorkflowId?: number | null;
    diagnosisWorkflowId?: number | null;
    caseWorkflowId?: number | null;
    spanWorkflowId?: number | null;
}) => {
    const map: Record<string, number[]> = {};

    const assign = (key: string, value?: number | null) => {
        if (typeof value === "number") {
            map[key] = [value];
        }
    };

    assign("PROCEDURE", payload.procedureWorkflowId ?? undefined);
    assign("MODIFIER", payload.modifierWorkflowId ?? undefined);
    assign("DIAGNOSIS", payload.diagnosisWorkflowId ?? undefined);
    assign("CASE", payload.caseWorkflowId ?? undefined);
    assign("SPAN", payload.spanWorkflowId ?? undefined);

    return map;
};

const normalizeAnswerValue = (value: AnswerValue | undefined): AnswerValue => {
    if (!value) {
        return {
            name: "",
            tagLabel: "",
            description: "",
            legacyValues: [],
        };
    }

    return {
        ...value,
        legacyValues: value.legacyValues ?? [],
    };
};

const normalizeAnswerSet = (answerSet: AnswerSet): AnswerSet => ({
    ...answerSet,
    answers: answerSet.answers?.map((value) => normalizeAnswerValue(value)),
});

const normalizeQuestion = (question: Question): Question => ({
    ...question,
    answers: question.answers?.map((value) => normalizeAnswerValue(value)),
});

const normalizeWorkflowTrigger = (trigger: WorkflowTrigger): WorkflowTrigger => ({
    ...trigger,
    answerValueIds: trigger.answerValueIds ?? [],
    action: (trigger.action as WorkflowTriggerAction) ?? WorkflowTriggerAction.GO_TO,
    triggerPhase: trigger.triggerPhase ?? "POST",
});

const normalizeWorkflowQuestion = (workflowQuestion: WorkflowQuestion): WorkflowQuestion => ({
    ...workflowQuestion,
    question: workflowQuestion.question ? normalizeQuestion(workflowQuestion.question) : workflowQuestion.question,
    triggers: workflowQuestion.triggers
        ?.map((trigger) => normalizeWorkflowTrigger(trigger))
        .sort((a, b) => (a.triggerOrder ?? 0) - (b.triggerOrder ?? 0)) ?? [],
});

const normalizeWorkflow = (workflow: CodeWorkflow): CodeWorkflow => ({
    ...workflow,
    questions: workflow.questions?.map((question) => normalizeWorkflowQuestion(question)),
});

export const fetchRubrics = async (active?: boolean): Promise<Rubric[]> => {
    const params = new URLSearchParams();
    if (active !== undefined) {
        params.append("active", String(active));
    }

    const response = await fetch(
        `/api/cqa-service/v1/rubrics${params.toString() ? `?${params.toString()}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    return handleJsonResponse<Rubric[]>(response);
};

export const fetchRubric = async (id: number): Promise<Rubric> => {
    const response = await fetch(`/api/cqa-service/v1/rubrics/${id}`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });

    return handleJsonResponse<Rubric>(response);
};

export const createRubric = async (payload: { name: string; description?: string }): Promise<Rubric> => {
    const response = await fetch(`/api/cqa-service/v1/rubrics`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(payload),
    });

    return handleJsonResponse<Rubric>(response);
};

export const updateRubric = async (
    id: number,
    payload: { name: string; description?: string; active?: boolean }
): Promise<Rubric> => {
    const response = await fetch(`/api/cqa-service/v1/rubrics/${id}`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(payload),
    });

    return handleJsonResponse<Rubric>(response);
};

export const deleteRubric = async (id: number): Promise<void> => {
    const response = await fetch(`/api/cqa-service/v1/rubrics/${id}`, {
        method: "DELETE",
        headers: buildAuthorizedJsonHeaders(),
    });

    await handleJsonResponse<void>(response);
};

export const cloneRubric = async (id: number, payload: CloneRubricPayload): Promise<Rubric> => {
    const response = await fetch(`/api/cqa-service/v1/rubrics/${id}/clone`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(payload),
    });

    return handleJsonResponse<Rubric>(response);
};

export const fetchRubricVersions = async (rubricId: number): Promise<RubricVersion[]> => {
    const response = await fetch(`/api/cqa-service/v1/rubrics/${rubricId}/versions`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });

    const versions = await handleJsonResponse<RubricVersion[]>(response);
    return versions?.map(normalizeRubricVersion);
};

export const fetchFullRubricVersion = async (versionId: number): Promise<FullRubricVersion> => {
    const response = await fetch(`/api/cqa-service/v1/rubric-versions/${versionId}/full`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });

    const payload = await handleJsonResponse<FullRubricVersion>(response);
    const normalizedWorkflows =
        payload.workflows?.map((workflowDetail) => ({
            ...workflowDetail,
            workflow: normalizeWorkflow(workflowDetail.workflow),
            questions: workflowDetail.questions?.map((question) => normalizeWorkflowQuestion(question)) ?? [],
        })) ?? [];

    return {
        ...payload,
        version: payload.version ? normalizeRubricVersion(payload.version) : payload.version,
        workflows: normalizedWorkflows,
    };
};

export const createRubricVersion = async (
    rubricId: number,
    payload: {
        majorVersion: string;
        minorVersion: string;
        description?: string;
        effectiveDate?: string;
        expirationDate?: string;
        procedureWorkflowId?: number | null;
        modifierWorkflowId?: number | null;
        diagnosisWorkflowId?: number | null;
        caseWorkflowId?: number | null;
        spanWorkflowId?: number | null;
        activeScoringConfigId?: number | null;
    }
): Promise<RubricVersion> => {
    const body: Record<string, unknown> = {
        majorVersion: payload.majorVersion,
        minorVersion: payload.minorVersion,
        effectiveDate: payload.effectiveDate,
        expirationDate: payload.expirationDate,
        codeWorkflows: buildCodeWorkflowPayload(payload),
        activeScoringConfigId: payload.activeScoringConfigId,
    };

    if (payload.description && payload.description.trim().length) {
        body.taskConfiguration = { description: payload.description.trim() };
    }

    const response = await fetch(`/api/cqa-service/v1/rubrics/${rubricId}/versions`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(body),
    });

    const version = await handleJsonResponse<RubricVersion>(response);
    return normalizeRubricVersion(version);
};

export const updateRubricVersion = async (
    versionId: number,
    payload: Partial<{
        status: RubricVersion["status"];
        majorVersion: string;
        minorVersion: string;
        description: string;
        effectiveDate: string;
        expirationDate: string;
        active: boolean;
        procedureWorkflowId: number | null;
        modifierWorkflowId: number | null;
        diagnosisWorkflowId: number | null;
        caseWorkflowId: number | null;
        spanWorkflowId: number | null;
        activeScoringConfigId: number | null;
    }>
): Promise<RubricVersion> => {
    const body: Record<string, unknown> = { ...payload };
    if (payload.description !== undefined) {
        if (payload.description && payload.description.trim().length) {
            body.taskConfiguration = { description: payload.description.trim() };
        } else {
            body.taskConfiguration = null;
        }
        delete body.description;
    }

    if (
        payload.procedureWorkflowId !== undefined ||
        payload.modifierWorkflowId !== undefined ||
        payload.diagnosisWorkflowId !== undefined ||
        payload.caseWorkflowId !== undefined ||
        payload.spanWorkflowId !== undefined
    ) {
        body.codeWorkflows = buildCodeWorkflowPayload({
            procedureWorkflowId: payload.procedureWorkflowId,
            modifierWorkflowId: payload.modifierWorkflowId,
            diagnosisWorkflowId: payload.diagnosisWorkflowId,
            caseWorkflowId: payload.caseWorkflowId,
            spanWorkflowId: payload.spanWorkflowId,
        });
    }

    const response = await fetch(`/api/cqa-service/v1/rubric-versions/${versionId}`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(body),
    });

    const version = await handleJsonResponse<RubricVersion>(response);
    return normalizeRubricVersion(version);
};

export const publishRubricVersion = async (versionId: number): Promise<RubricVersion> => {
    const response = await fetch(`/api/cqa-service/v1/rubric-versions/${versionId}/publish`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
    });

    const version = await handleJsonResponse<RubricVersion>(response);
    return normalizeRubricVersion(version);
};

export const deleteRubricVersion = async (versionId: number): Promise<void> => {
    const response = await fetch(`/api/cqa-service/v1/rubric-versions/${versionId}`, {
        method: "DELETE",
        headers: buildAuthorizedJsonHeaders(),
    });

    await handleJsonResponse<void>(response);
};

export const cloneRubricVersion = async (
    versionId: number,
    payload?: CloneRubricVersionPayload
): Promise<RubricVersion> => {
    const response = await fetch(`/api/cqa-service/v1/rubric-versions/${versionId}/clone`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: payload ? JSON.stringify(payload) : "{}",
    });

    const version = await handleJsonResponse<RubricVersion>(response);
    return normalizeRubricVersion(version);
};

// Resolver config (CDMX-11298-2): list by name, get/set active for rubric version
export interface ResolverConfigSummary {
    id: number;
    name: string;
    description?: string | null;
}

export interface ResolverConfigPageResponse {
    content: ResolverConfigSummary[];
    page: number;
    size: number;
    totalElements?: number;
    totalPages?: number;
}

export async function listResolverConfigs(params?: {
    rubricVersionId?: number;
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
}): Promise<ResolverConfigPageResponse> {
    const searchParams = new URLSearchParams();
    if (params?.rubricVersionId != null) searchParams.set("rubricVersionId", String(params.rubricVersionId));
    if (params?.search) searchParams.set("search", params.search);
    if (params?.page != null) searchParams.set("page", String(params.page));
    if (params?.size != null) searchParams.set("size", String(params.size));
    if (params?.sortBy) searchParams.set("sortBy", params.sortBy);
    if (params?.sortDirection) searchParams.set("sortDirection", params.sortDirection);
    const query = searchParams.toString();
    const response = await fetch(
        `/api/cqa-service/v1/resolver/config/search${query ? `?${query}` : ""}`,
        { method: "GET", headers: buildAuthorizedJsonHeaders() }
    );
    return handleJsonResponse<ResolverConfigPageResponse>(response);
}

export async function getActiveResolverConfig(rubricVersionId: number): Promise<ResolverConfigSummary | null> {
    const response = await fetch(
        `/api/cqa-service/v1/resolver/config/active?rubricVersionId=${rubricVersionId}`,
        { method: "GET", headers: buildAuthorizedJsonHeaders() }
    );
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Failed to get active resolver config: ${response.status}`);
    return handleJsonResponse<ResolverConfigSummary>(response);
}

export async function setActiveResolverConfig(payload: SetActiveResolverConfigRequest): Promise<void> {
    const body: Record<string, unknown> = { rubricVersionId: payload.rubricVersionId };
    if (payload.resolverConfigId !== undefined) {
        body.resolverConfigId = payload.resolverConfigId ?? null;
    }
    const response = await fetch(`/api/cqa-service/v1/resolver/config/active`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to set active resolver config: ${response.status}`);
    if (response.status !== 204) await response.text();
}

// Scoring configs (CDMX-15222): list active configs for rubric version association
export interface ScoringConfigSummary {
    id: number;
    name: string;
    description?: string | null;
}

export async function listScoringConfigs(): Promise<ScoringConfigSummary[]> {
    const response = await fetch(`/api/cqa-service/v1/scoring-configs`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });
    return handleJsonResponse<ScoringConfigSummary[]>(response);
}

export async function listScoringEvents(workItemId: number): Promise<unknown[]> {
    const response = await fetch(`/api/cqa-service/v1/work-items/${workItemId}/scoring-events`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });
    return handleJsonResponse(response);
}

export async function listGraderScoringEvents(sdCaseId: string): Promise<unknown[]> {
    const response = await fetch(
        `/api/cqa-service/v1/grader-workitem/${encodeURIComponent(sdCaseId)}/scoring-events`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        },
    );
    return handleJsonResponse(response);
}

const buildWorkflowQuestionsPayload = (questions?: WorkflowQuestion[]): Record<string, unknown>[] | undefined => {
    if (!questions) {
        return undefined;
    }
    return questions.map((assignment, index) => ({
        questionId: assignment.question.id,
        sequenceOrder: assignment.sequenceOrder ?? index + 1,
        spanScope: assignment.spanScope ?? "ALL_SPANS",
        spanScopeQuestionId: assignment.spanScope === "SELECTED_BY_SPANS_QUESTION"
            ? assignment.spanScopeQuestionId ?? null
            : null,
        triggers: assignment.triggers
            ?.filter((trigger) => {
                const action = (trigger.action ?? "GO_TO") as WorkflowTrigger["action"];
                const triggerPhase = trigger.triggerPhase ?? "POST";
                if (triggerPhase === "PRE" && action === WorkflowTriggerAction.SHOW_QUESTION) {
                    return true;
                }
                if (action === "STOP") {
                    return true;
                }
                return Boolean(trigger.toQuestionId);
            })
            .map((trigger, triggerIdx) => {
                const hasCompoundCondition = Boolean(trigger.compoundCondition);
                const action = trigger.action ?? WorkflowTriggerAction.GO_TO;
                const triggerPhase = trigger.triggerPhase ?? "POST";
                const isShowWhen = triggerPhase === "PRE" && action === WorkflowTriggerAction.SHOW_QUESTION;
                return {
                    id: trigger.id,
                    hostQuestionId: trigger.hostQuestionId ?? assignment.question.id,
                    fromQuestionId: hasCompoundCondition ? undefined : trigger.fromQuestionId,
                    answerValueIds: hasCompoundCondition ? undefined : trigger.answerValueIds,
                    targetQuestionId: action === "STOP" || isShowWhen ? undefined : trigger.toQuestionId,
                    active: trigger.action ? true : undefined,
                    action,
                    triggerPhase,
                    toAnswerValueIds: isShowWhen ? undefined : trigger.toAnswerValueIds ?? [],
                    triggerOrder: trigger.triggerOrder ?? triggerIdx,
                    compoundCondition: trigger.compoundCondition ?? undefined,
                };
            }),
    }));
};

type CodeWorkflowCreatePayload = {
    name: string;
    codeType: CodeWorkflowType;
    description?: string | null;
    active?: boolean | null;
    questions?: WorkflowQuestion[];
};

export type CodeWorkflowUpdatePayload = Partial<CodeWorkflowCreatePayload>;

export const listCodeWorkflows = async (params?: {
    rubricVersionId?: number;
    codeType?: CodeWorkflowType;
    active?: boolean;
}): Promise<CodeWorkflow[]> => {
    const searchParams = new URLSearchParams();
    if (params?.rubricVersionId) {
        searchParams.append("rubricVersionId", String(params.rubricVersionId));
    }
    if (params?.codeType) {
        searchParams.append("codeType", params.codeType);
    }
    if (params?.active !== undefined) {
        searchParams.append("active", String(params.active));
    }

    const response = await fetch(
        `/api/cqa-service/v1/code-workflows${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    const workflows = await handleJsonResponse<CodeWorkflow[]>(response);
    return workflows?.map(normalizeWorkflow);
};

export const fetchCodeWorkflow = async (id: number): Promise<CodeWorkflow> => {
    const response = await fetch(`/api/cqa-service/v1/code-workflows/${id}`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });

    const workflow = await handleJsonResponse<CodeWorkflow>(response);
    return normalizeWorkflow(workflow);
};

export const createCodeWorkflow = async (payload: CodeWorkflowCreatePayload): Promise<CodeWorkflow> => {
    const body: Record<string, unknown> = {
        name: payload.name,
        codeType: payload.codeType,
        description: payload.description,
        active: payload.active,
    };
    const questionsPayload = buildWorkflowQuestionsPayload(payload.questions);
    if (questionsPayload) {
        body.questions = questionsPayload;
    }

    const response = await fetch(`/api/cqa-service/v1/code-workflows`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(body),
    });

    const workflow = await handleJsonResponse<CodeWorkflow>(response);
    return normalizeWorkflow(workflow);
};

export const updateCodeWorkflow = async (
    id: number,
    payload: CodeWorkflowUpdatePayload
): Promise<CodeWorkflow> => {
    const body: Record<string, unknown> = { ...payload };
    if (payload.questions) {
        body.questions = buildWorkflowQuestionsPayload(payload.questions);
    }

    const response = await fetch(`/api/cqa-service/v1/code-workflows/${id}`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(body),
    });

    const workflow = await handleJsonResponse<CodeWorkflow>(response);
    return normalizeWorkflow(workflow);
};

export const publishCodeWorkflow = async (id: number): Promise<CodeWorkflow> => {
    const response = await fetch(`/api/cqa-service/v1/code-workflows/${id}/publish`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
    });

    const workflow = await handleJsonResponse<CodeWorkflow>(response);
    return normalizeWorkflow(workflow);
};

export const deleteCodeWorkflow = async (id: number): Promise<void> => {
    const response = await fetch(`/api/cqa-service/v1/code-workflows/${id}`, {
        method: "DELETE",
        headers: buildAuthorizedJsonHeaders(),
    });

    await handleJsonResponse<void>(response);
};

export const resolveResolverStateless = async (payload: StatelessResolverRequest): Promise<ResolverResponse> => {
    const response = await fetch(`/api/cqa-service/v1/resolver/resolve`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(payload),
    });

    return handleJsonResponse<ResolverResponse>(response);
};

export interface ListResolverConfigsParams {
    rubricVersionId?: number;
    active?: boolean;
    search?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
}

export const listResolverConfigsPaginated = async (
    params: ListResolverConfigsParams = {}
): Promise<PageResponse<ResolverConfigDto>> => {
    const searchParams = new URLSearchParams();
    if (params.rubricVersionId !== undefined) {
        searchParams.append("rubricVersionId", String(params.rubricVersionId));
    }
    if (params.active !== undefined) {
        searchParams.append("active", String(params.active));
    }
    if (params.search) {
        searchParams.append("search", params.search);
    }
    if (params.page !== undefined) {
        searchParams.append("page", String(params.page));
    }
    if (params.size !== undefined) {
        searchParams.append("size", String(params.size));
    }
    if (params.sortBy) {
        searchParams.append("sortBy", params.sortBy);
    }
    if (params.sortDirection) {
        searchParams.append("sortDirection", params.sortDirection);
    }
    const queryString = searchParams.toString();
    const response = await fetch(
        `/api/cqa-service/v1/resolver/config/search${queryString ? `?${queryString}` : ""}`,
        { method: "GET", headers: buildAuthorizedJsonHeaders() }
    );
    return handleJsonResponse<PageResponse<ResolverConfigDto>>(response);
};

export const getResolverConfig = async (id: number): Promise<ResolverConfigDto> => {
    const response = await fetch(`/api/cqa-service/v1/resolver/config/${id}`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });
    return handleJsonResponse<ResolverConfigDto>(response);
};

export const createResolverConfig = async (payload: {
    name: string;
    description?: string;
    config: ResolverConfigDto;
}): Promise<ResolverConfigDto> => {
    const { config, ...rest } = payload;
    const body = { ...rest, config: config.id !== undefined ? { ...config, id: undefined } : config };
    const response = await fetch(`/api/cqa-service/v1/resolver/config`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(body),
    });
    return handleJsonResponse<ResolverConfigDto>(response);
};

export const updateResolverConfig = async (
    id: number,
    payload: { name?: string; description?: string; config?: ResolverConfigDto }
): Promise<ResolverConfigDto> => {
    const response = await fetch(`/api/cqa-service/v1/resolver/config/${id}`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(payload),
    });
    return handleJsonResponse<ResolverConfigDto>(response);
};

export const deleteResolverConfig = async (id: number): Promise<void> => {
    const response = await fetch(`/api/cqa-service/v1/resolver/config/${id}`, {
        method: "DELETE",
        headers: buildAuthorizedJsonHeaders(),
    });
    await handleJsonResponse<void>(response);
};

type AnswerSetPayload = {
    name: string;
    description?: string;
    active?: boolean;
    answers?: AnswerValue[];
};

const mapAnswerSetPayload = (payload: AnswerSetPayload): Record<string, unknown> => {
    const body: Record<string, unknown> = {
        name: payload.name,
        description: payload.description,
    };

    if (payload.active !== undefined) {
        body.active = payload.active;
    }

    if (payload.answers) {
        body.answers = payload.answers.map((answer) => ({
            id: answer.id,
            name: answer.name,
            tagLabel: answer.tagLabel,
            description: answer.description,
            legacyValues: answer.legacyValues ?? [],
            active: answer.active,
        }));
    }

    return body;
};

export const fetchAnswerSets = async (params?: { active?: boolean }): Promise<AnswerSet[]> => {
    const searchParams = new URLSearchParams();
    if (params?.active !== undefined) {
        searchParams.append("active", String(params.active));
    }

    const response = await fetch(
        `/api/cqa-service/v1/answer-sets${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    const sets = await handleJsonResponse<AnswerSet[]>(response);
    return sets?.map((set) => normalizeAnswerSet(set)) ?? [];
};

export const fetchAnswerSet = async (id: number): Promise<AnswerSet> => {
    const response = await fetch(`/api/cqa-service/v1/answer-sets/${id}`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });

    const answerSet = await handleJsonResponse<AnswerSet>(response);
    return normalizeAnswerSet(answerSet);
};

export const createAnswerSet = async (payload: AnswerSetPayload): Promise<AnswerSet> => {
    const response = await fetch(`/api/cqa-service/v1/answer-sets`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(mapAnswerSetPayload(payload)),
    });

    const answerSet = await handleJsonResponse<AnswerSet>(response);
    return normalizeAnswerSet(answerSet);
};

export const updateAnswerSet = async (id: number, payload: AnswerSetPayload): Promise<AnswerSet> => {
    const response = await fetch(`/api/cqa-service/v1/answer-sets/${id}`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(mapAnswerSetPayload(payload)),
    });

    const answerSet = await handleJsonResponse<AnswerSet>(response);
    return normalizeAnswerSet(answerSet);
};

export const deleteAnswerSet = async (id: number): Promise<void> => {
    const response = await fetch(`/api/cqa-service/v1/answer-sets/${id}`, {
        method: "DELETE",
        headers: buildAuthorizedJsonHeaders(),
    });

    await handleJsonResponse<void>(response);
};

type QuestionPayload = {
    name: string;
    questionText: string;
    questionType: Question["questionType"];
    required?: boolean;
    answerSetId?: number | null;
    displayOrder?: number | null;
    active?: boolean;
    selectedAnswerIds?: number[];
    inclusionMode?: InclusionMode;
    expression?: string | null;
};

const mapQuestionPayload = (payload: QuestionPayload): Record<string, unknown> => {
    const body: Record<string, unknown> = {
        name: payload.name,
        questionText: payload.questionText,
        questionType: payload.questionType,
        required: payload.required ?? false,
        answerSetId: payload.answerSetId ?? null,
        selectedAnswerIds: payload.selectedAnswerIds ?? [],
        inclusionMode: payload.inclusionMode ?? InclusionMode.EXCLUDE,
    };

    if (payload.displayOrder !== undefined && payload.displayOrder !== null) {
        body.displayOrder = payload.displayOrder;
    }
    if (payload.active !== undefined) {
        body.active = payload.active;
    }
    if (payload.expression !== undefined) {
        body.expression = payload.expression;
    }

    return body;
};

export const fetchQuestions = async (params?: { active?: boolean }): Promise<Question[]> => {
    const searchParams = new URLSearchParams();
    if (params?.active !== undefined) {
        searchParams.append("active", String(params.active));
    }

    const response = await fetch(
        `/api/cqa-service/v1/questions${searchParams.toString() ? `?${searchParams.toString()}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    const questions = await handleJsonResponse<Question[]>(response);
    return questions?.map((question) => normalizeQuestion(question)) ?? [];
};

export const fetchQuestion = async (id: number): Promise<Question> => {
    const response = await fetch(`/api/cqa-service/v1/questions/${id}`, {
        method: "GET",
        headers: buildAuthorizedJsonHeaders(),
    });

    const question = await handleJsonResponse<Question>(response);
    return normalizeQuestion(question);
};

export const createQuestion = async (payload: QuestionPayload): Promise<Question> => {
    const response = await fetch(`/api/cqa-service/v1/questions`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(mapQuestionPayload(payload)),
    });

    const question = await handleJsonResponse<Question>(response);
    return normalizeQuestion(question);
};

export const updateQuestion = async (id: number, payload: QuestionPayload): Promise<Question> => {
    const response = await fetch(`/api/cqa-service/v1/questions/${id}`, {
        method: "PUT",
        headers: buildAuthorizedJsonHeaders(),
        body: JSON.stringify(mapQuestionPayload(payload)),
    });

    const question = await handleJsonResponse<Question>(response);
    return normalizeQuestion(question);
};

export const publishQuestion = async (id: number): Promise<Question> => {
    const response = await fetch(`/api/cqa-service/v1/questions/${id}/publish`, {
        method: "POST",
        headers: buildAuthorizedJsonHeaders(),
    });

    const question = await handleJsonResponse<Question>(response);
    return normalizeQuestion(question);
};

export const deleteQuestion = async (id: number): Promise<void> => {
    const response = await fetch(`/api/cqa-service/v1/questions/${id}`, {
        method: "DELETE",
        headers: buildAuthorizedJsonHeaders(),
    });

    await handleJsonResponse<void>(response);
};

// Paginated response type
export interface PageResponse<T> {
    content: T[];
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
    sortBy?: string;
    sortDirection?: string;
    searchQuery?: string;
}

// Paginated search parameters
export interface PaginatedSearchParams {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    search?: string;
    active?: boolean;
}

const buildPaginatedSearchParams = (params: PaginatedSearchParams): URLSearchParams => {
    const searchParams = new URLSearchParams();

    if (params.page !== undefined && params.page > 0) {
        searchParams.append("page", String(params.page));
    }
    if (params.size !== undefined && params.size !== 10) {
        searchParams.append("size", String(params.size));
    }
    if (params.sortBy) {
        searchParams.append("sortBy", params.sortBy);
    }
    if (params.sortDirection) {
        searchParams.append("sortDirection", params.sortDirection);
    }
    if (params.search) {
        searchParams.append("search", params.search);
    }
    if (params.active !== undefined) {
        searchParams.append("active", String(params.active));
    }

    return searchParams;
};

/**
 * Fetch paginated questions with search and sorting
 */
export const fetchQuestionsPaginated = async (
    params: PaginatedSearchParams = {}
): Promise<PageResponse<Question>> => {
    const searchParams = buildPaginatedSearchParams(params);
    const queryString = searchParams.toString();

    const response = await fetch(
        `/api/cqa-service/v1/questions/search${queryString ? `?${queryString}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    const pageResponse = await handleJsonResponse<PageResponse<Question>>(response);
    return {
        ...pageResponse,
        content: pageResponse.content?.map((q) => normalizeQuestion(q)) ?? [],
    };
};

/**
 * Fetch paginated code workflows with search and sorting
 */
export const listCodeWorkflowsPaginated = async (
    params: PaginatedSearchParams & { codeType?: string } = {}
): Promise<PageResponse<CodeWorkflow>> => {
    const searchParams = buildPaginatedSearchParams(params);

    if (params.codeType) {
        searchParams.append("codeType", params.codeType);
    }

    const queryString = searchParams.toString();

    const response = await fetch(
        `/api/cqa-service/v1/code-workflows/search${queryString ? `?${queryString}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    const pageResponse = await handleJsonResponse<PageResponse<CodeWorkflow>>(response);
    return {
        ...pageResponse,
        content: pageResponse.content?.map((w) => normalizeWorkflow(w)) ?? [],
    };
};

/**
 * Fetch paginated answer sets with search and sorting
 */
export const fetchAnswerSetsPaginated = async (
    params: PaginatedSearchParams = {}
): Promise<PageResponse<AnswerSet>> => {
    const searchParams = buildPaginatedSearchParams(params);
    const queryString = searchParams.toString();

    const response = await fetch(
        `/api/cqa-service/v1/answer-sets/search${queryString ? `?${queryString}` : ""}`,
        {
            method: "GET",
            headers: buildAuthorizedJsonHeaders(),
        }
    );

    const pageResponse = await handleJsonResponse<PageResponse<AnswerSet>>(response);
    return {
        ...pageResponse,
        content: pageResponse.content?.map((set) => normalizeAnswerSet(set)) ?? [],
    };
};

export const fetchRiskCategories =  async (): Promise<CQARiskDictionaries | null> => {
    const todaysDate = (new Date()).toISOString().split('T')[0]; // YYYY-MM-DD format
    const _modRowData = await fetch(
        `/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesMODV2`,
        {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            }
        }
    );
    const _cptRowData = await fetch(
        `/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesCPT`,
        {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            }
        }
    );
    const _icdRowData = await fetch(
      `/dictionary/row/unrestricted/v2?tenantId=BB4D9AA3-3047-4542-8FB1-8C9BD6E4D75C&effectiveDate=${todaysDate}&dictionaryName=TempRiskCategoriesICD`,
        {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            }
        }
    );

    if (!_modRowData.ok) {
        console.error("Error Fetching Risk Categories MODV2");
        return null;
    }

    if (!_cptRowData.ok) {
        console.error("Error Fetching Risk Categories CPT");
        return null;
    }

    if (!_icdRowData.ok) {
        console.error("Error Fetching Risk Categories ICD");
        return null;
    }

    const modRowData = await _modRowData.json();
    const cptRowData = await _cptRowData.json();
    const icdRowData = await _icdRowData.json();

    return {
      modifier: modRowData,
      procedure: cptRowData,
      diagnosis: icdRowData
    };
};

export interface ConfigurationValue {
    id: number | null;
    value: string;
    jsonValue: Record<string, any>;
    tenantId: string;
    configurationKeyname: string | null;
    configurationId: number | null;
    version: string | null;
    serviceLines: string[];
    sourceType: string | null;
    createdDate: number | null;
    updatedDate: number | null;
    jiraTicket: string | null;
    jiraPurpose: string | null;
}

export interface ConfigurationResponse {
    id: number;
    section: string;
    keyname: string;
    version: string | null;
    description: string;
    multiValue: boolean | null;
    dataType: string | null;
    status: string | null;
    displayName: string | null;
    healthSystemLevel: string | null;
    createdDate: number | null;
    updatedDate: number | null;
    values: ConfigurationValue[];
}

/**
 * Fetch merged configuration JSON value
 */
export async function fetchMergedConfigurationJsonValue(
    configurationId: string,
    tenantId: string,
    serviceLine: string
): Promise<Record<string, any>> {
    const effectiveServiceLine = reverseServiceLineMapper[serviceLine] ?? serviceLine;
    const endpoint = `/configuration/detail/${configurationId}/${tenantId}/v2?merge=true&serviceLine=${effectiveServiceLine}`;

    try {
        // Use the same fetch pattern as existing service desk code
        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            }
        });

        if (!response.ok) {
            console.warn(
                `[fetchMergedConfigurationJsonValue] HTTP error ${response.status} for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return {};
        }

        const responseData: ConfigurationResponse[] = await response.json();

        if (!Array.isArray(responseData) || responseData.length === 0) {
            console.warn(
                `[fetchMergedConfigurationJsonValue] No configuration found for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return {};
        }

        const configObj = responseData[0];
        if (!configObj || !Array.isArray(configObj.values)) {
            console.warn(
                `[fetchMergedConfigurationJsonValue] Config object missing or values not array for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return {};
        }

        // Find the MERGED value - this is the final merged configuration
        const mergedValueObj = configObj.values.find(
            (v: ConfigurationValue) => v.value === 'MERGED'
        );

        if (!mergedValueObj || !mergedValueObj.jsonValue) {
            console.warn(
                `[fetchMergedConfigurationJsonValue] No merged value found for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return {};
        }

        return mergedValueObj.jsonValue;
    } catch (error) {
        console.error(
            `[fetchMergedConfigurationJsonValue] Error fetching configuration for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}:`,
            error
        );
        return {};
    }
}

/**
 * Helper function to check if a service line matches (inspired by ui-omnibus configuration-utils)
 */
function serviceLineMatches(
    configValue: ConfigurationValue,
    serviceLineName: string,
    includeWildcard: boolean = true
): boolean {
    const serviceLines = configValue.serviceLines || [];
    return (
        serviceLines.includes(serviceLineName) ||
        (includeWildcard && serviceLines.includes("*"))
    );
}

/**
 * Helper function to get display value, prioritizing jsonValue over value (inspired by ui-omnibus configuration-utils)
 */
function getConfigDisplayValue(configValue: ConfigurationValue): any {
    // If jsonValue exists and is not null/undefined, return it
    if (configValue.jsonValue !== null && configValue.jsonValue !== undefined) {
        return configValue.jsonValue;
    }
    // Fall back to the regular value
    return configValue.value;
}

function compareConfigValueRecency(
    left: ConfigurationValue,
    right: ConfigurationValue
): number {
    const leftKeys = [left.updatedDate ?? 0, left.createdDate ?? 0, left.id ?? 0];
    const rightKeys = [right.updatedDate ?? 0, right.createdDate ?? 0, right.id ?? 0];

    for (let index = 0; index < leftKeys.length; index += 1) {
        if (leftKeys[index] !== rightKeys[index]) {
            return rightKeys[index] - leftKeys[index];
        }
    }

    return 0;
}

/**
 * Fetch resolved configuration value for simple string configurations
 * This function looks through the values array to find the highest priority match
 * based on tenant and serviceLine, following the same priority logic as ui-omnibus configuration-utils.
 */
export async function fetchResolvedConfigurationValue(
    configurationId: string,
    tenantId: string,
    serviceLine: string
): Promise<string | null> {
    const effectiveServiceLine = reverseServiceLineMapper[serviceLine] ?? serviceLine;
    // Don't use merge=true for simple string values - we need to look through the values array
    const endpoint = `/configuration/detail/${configurationId}/${tenantId}/v2?merge=false&serviceLine=${effectiveServiceLine}`;

    try {
        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `Bearer ${sessionStorage.getItem("authToken")}`,
            }
        });

        if (!response.ok) {
            console.warn(
                `[fetchResolvedConfigurationValue] HTTP error ${response.status} for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return null;
        }

        const responseData: ConfigurationResponse[] = await response.json();

        if (!Array.isArray(responseData) || responseData.length === 0) {
            console.warn(
                `[fetchResolvedConfigurationValue] No configuration found for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return null;
        }

        const configObj = responseData[0];
        if (!configObj || !Array.isArray(configObj.values)) {
            console.warn(
                `[fetchResolvedConfigurationValue] Config object missing or values not array for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}`
            );
            return null;
        }

        const values = configObj.values;

        // Define match criteria in priority order (highest priority first)
        // Following the same pattern as ui-omnibus configuration-utils
        const matchCriteria = [
            // 1. Exact tenant + Exact service line (highest priority)
            (v: ConfigurationValue) =>
                v.tenantId === tenantId && serviceLineMatches(v, effectiveServiceLine, false),

            // 2. Exact tenant + Wildcard service line
            (v: ConfigurationValue) =>
                v.tenantId === tenantId && v.serviceLines?.includes("*"),

            // 3. Parent health system + Exact service line
            (v: ConfigurationValue) =>
                v.tenantId !== tenantId && v.tenantId !== "*" && serviceLineMatches(v, effectiveServiceLine, false),

            // 4. Parent health system + Wildcard service line
            (v: ConfigurationValue) =>
                v.tenantId !== tenantId && v.tenantId !== "*" && v.serviceLines?.includes("*"),

            // 5. Wildcard tenant + Exact service line
            (v: ConfigurationValue) =>
                v.tenantId === "*" && serviceLineMatches(v, effectiveServiceLine, false),

            // 6. Wildcard tenant + Wildcard service line (lowest priority)
            (v: ConfigurationValue) =>
                v.tenantId === "*" && v.serviceLines?.includes("*")
        ];

        // Find the first matching criteria that has results
        for (const criteria of matchCriteria) {
            const matches = values.filter(criteria);
            if (matches.length > 0) {
                const matchedValue = [...matches].sort(compareConfigValueRecency)[0];
                const displayValue = getConfigDisplayValue(matchedValue);
                // Convert to string if it's not already
                return displayValue === null || displayValue === undefined ? null : String(displayValue);
            }
        }

        return null;
    } catch (error) {
        console.error(
            `[fetchResolvedConfigurationValue] Error fetching configuration for configurationId=${configurationId}, tenantId=${tenantId}, serviceLine=${effectiveServiceLine}:`,
            error
        );
        return null;
    }
}


/**
 * Fetch worklist filter options configuration
 * This will be called when a worklist item loads
 */
export async function fetchWorklistFilterOptions(
  tenantId: string,
  serviceLine: string,
  isQualityAssessment: boolean
): Promise<Record<string, any>> {
  if (isQualityAssessment) {
    return Promise.resolve(cqaWorklistSearchFieldOptions);
  } else {
    return fetchMergedConfigurationJsonValue(
      "worklist-column-filter-options",
      tenantId,
      serviceLine
    );
  }
}

export const apiReassignCQAWorkitems = async (reassignments: Array<{ assignee_id: string; sd_case_id: string }>) => {
    const response = await fetch(`/api/cqa-service/v1/grader-workitem/reassign`, {
        method: 'PUT',
        headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ reassignments }),
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to reassign CQA workitems: ${response.statusText} - ${errorText}`);
    }
    
    return response.ok;
};

/**
 * Fetch case transfer permissions configuration
 * This will be called when a worklist item loads
 */
export async function fetchCaseTransferPermissionsConfigOptions(
    tenantId: string,
    serviceLine: string
): Promise<Record<string, any>> {
    return fetchMergedConfigurationJsonValue(
        'case-transfer-permissions',
        tenantId,
        serviceLine
    );
}

export const cqaAssignGroup = async (ruleId: string, groupId: string): Promise<{"updatedCount": number, "message": string}> => {
    const errorMessage = `Error PUT /api/cqa-service/v1/project/${ruleId}/assign-group`;

    try {
        const response = await fetch(`/api/cqa-service/v1/project/${ruleId}/assign-group`, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            },
            method: "PUT",
            body: JSON.stringify({
                groupId,
            })
        });

        if (!response.ok) {
            console.error(errorMessage);
            throw new Error(errorMessage);
        }

        const body = await response.json();
        return body;
    }
    catch (e) {
        console.error(errorMessage, e);
        throw new Error(errorMessage);
    }
};

export const CQA_ASSIGN_LABEL_MAPPING = {
    [LABEL_SPECIFIC_COUNT]: "COUNT",
    [LABEL_SPECIFIC_PERCENTAGE]: "PERCENTAGES",
    [LABEL_EQUAL_PERCENTAGES]: "EQUAL_PERCENTAGE",
    [LABEL_MULTI_USER]: "MULTI_USER",
}

export const cqaAssignCoders = async (ruleId: string, volumeType: string, individuals: OptionEntry[]): Promise<{"updatedCount": number, "message": string}> => {
    const errorMessage = `Error PUT /api/cqa-service/v1/project/${ruleId}/assign-coders`;

    try {
        const response = await fetch(`/api/cqa-service/v1/project/${ruleId}/assign-coders`, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            },
            method: "PUT",
            body: JSON.stringify({
                volumeType,
                assignees: individuals.map((ind) => ({ assigneeId: ind.value, volume: ind.volume }))
            })
        });

        if (!response.ok) {
            console.error(errorMessage);
            throw new Error(errorMessage);
        }

        const body = await response.json();
        return body;
    }
    catch (e) {
        console.error(errorMessage, e);
        throw new Error(errorMessage, e);
    }
};

export const cqaDistributionMetadata = async (ruleId: string): Promise<{"assignable_count": number, "unassigned_count": number, "processing_count": number}> => {
    const errorMessage = `Error GET /api/cqa-service/v1/project/${ruleId}/metadata`;
    try {
        const response = await fetch(`/api/cqa-service/v1/project/${ruleId}/metadata`, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            },
            method: "GET",
        });

        if (!response.ok) {
            console.error(errorMessage);
            throw new Error(`Error fetching unassigned workitems`);
        }

        const body = await response.json();
        return body;
    }
    catch (e) {
        console.error(errorMessage, e);
        throw new Error(`Error fetching unassigned workitems`);
    }
};

export const cqaRubricVersionChangeImpact = async (ruleId: string): Promise<ProjectRubricVersionChangeImpact> => {
    const errorMessage = `Error GET /api/cqa-service/v1/project/${ruleId}/rubric-version-change-impact`;
    try {
        const response = await fetch(`/api/cqa-service/v1/project/${ruleId}/rubric-version-change-impact`, {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": `${sessionStorage.getItem("authToken")}`,
            },
            method: "GET",
        });

        if (!response.ok) {
            console.error(errorMessage);
            throw new Error(`Error fetching rubric version change impact`);
        }

        return await response.json();
    }
    catch (e) {
        console.error(errorMessage, e);
        throw new Error(`Error fetching rubric version change impact`);
    }
};
